import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { connect } from 'react-redux';
import { createPortal } from 'react-dom';
import { useStore } from '@nanostores/react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { map } from 'lodash';
import { $isFullscreenAtom, $isPausedAtom, $preNeedWidget, $userStatusAtom } from '~/atoms';
import { Feature } from '~/types/common';
import Api from '~/api';
import { styles } from './viewerCommercialLinks.styles';
import ViewerImgButton from '~/elements/ViewerImgButton';
import { $isTributeButtonHovered } from '~/atoms/isTributeButtonHovered';
import { ViewerPlanAheadButton } from '~/elements/ViewerImgButton/viewerImgButton';
import { featureOn } from '~/utils/featureToggle';
import { isPlanAheadAvailable, isPlanAheadButton } from '~/utils/helpers';
const PLAN_AHEAD_ENABLED = featureOn(Feature.plan_ahead);
const PLAN_AHEAD_BUTTON = isPlanAheadButton();
// Styled component for the wrapper
const Div = styled(motion.div) `
  ${styles}
`;
function calculateHeight() {
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    return Math.max(0, (windowHeight - (windowWidth * 9) / 16) / 2);
}
function getHeight() {
    const controlbar = document.querySelector('.jw-controlbar');
    const calculatedHeight = calculateHeight();
    const defaultHeight = calculatedHeight === 0 ? '16px' : 'calc(((100vh - (100vw * 9 / 16)) / 2) + 16px)';
    if (!controlbar)
        return defaultHeight;
    const controlbarHeight = controlbar.clientHeight;
    const height = controlbarHeight > calculatedHeight ? `calc(${controlbarHeight}px + 16px)` : defaultHeight;
    return height;
}
// Main component
const ViewerCommercialLinks = ({ commercialLinks, page, eventStatus, ...rest }) => {
    const handleAnalyticClick = (event) => {
        Api.Analytics.commercialClick(event.currentTarget.id, page, eventStatus);
    };
    const player = document.querySelector('#jwplayer-0');
    const userStatus = useStore($userStatusAtom);
    const isFullscreen = useStore($isFullscreenAtom);
    const isTributeButtonHovered = useStore($isTributeButtonHovered);
    const isPaused = useStore($isPausedAtom);
    const [controlbarHeight, setControlbarHeight] = React.useState(getHeight);
    const [calculatedHeight, setCalculatedHeight] = React.useState(calculateHeight);
    function handleHeightChange() {
        const calc = calculateHeight();
        const height = getHeight();
        setCalculatedHeight(calc);
        setControlbarHeight(height);
    }
    // Mutation observer for user activity status
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (mutation.type === 'attributes') {
                if (mutation.target.classList.contains('jw-flag-user-inactive')) {
                    if (isTributeButtonHovered && player) {
                        player.classList.remove('jw-flag-user-inactive');
                    }
                    else {
                        $userStatusAtom.set('inactive');
                        if (!isPaused) {
                            setControlbarHeight(calculatedHeight === 0 ? '16px' : 'calc(((100vh - (100vw * 9 / 16)) / 2) + 16px)');
                        }
                    }
                }
                else {
                    $userStatusAtom.set('active');
                    handleHeightChange();
                }
            }
        });
    });
    React.useEffect(() => {
        if (player && isFullscreen) {
            observer.observe(player, {
                attributes: true,
                attributeFilter: ['class'],
                childList: false,
                characterData: false,
            });
        }
        return () => {
            observer.disconnect();
        };
    }, [player, isFullscreen, observer]);
    React.useEffect(() => {
        window.addEventListener('resize', handleHeightChange);
        return () => {
            window.removeEventListener('resize', handleHeightChange);
        };
    }, []);
    React.useEffect(() => {
        if (isPaused && userStatus === 'inactive') {
            handleHeightChange();
        }
        if (!isPaused && userStatus === 'inactive') {
            setControlbarHeight(calculatedHeight === 0 ? '16px' : 'calc(((100vh - (100vw * 9 / 16)) / 2) + 16px)');
        }
    }, [isPaused, userStatus, setControlbarHeight]);
    const handlePreNeedAction = () => {
        const jwplayer = window.jwplayer('jwplayer-0');
        if (jwplayer && jwplayer.getFullscreen()) {
            jwplayer.setFullscreen(false);
        }
        $preNeedWidget.set(true);
    };
    const isTestEnv = process.env.NODE_ENV === 'test';
    const planAheadButton = isTestEnv
        ? false
        : PLAN_AHEAD_ENABLED && PLAN_AHEAD_BUTTON && isPlanAheadAvailable();
    if (!player || !isFullscreen)
        return null;
    if (!commercialLinks && !planAheadButton)
        return null;
    const filteredCommercialLinks = commercialLinks && PLAN_AHEAD_ENABLED
        ? Object.fromEntries(Object.entries(commercialLinks).filter(([category]) => category !== 'PRE_PLANNING'))
        : commercialLinks;
    return createPortal(_jsx(Div, { layout: true, initial: { opacity: 0 }, animate: { opacity: 1 }, transition: {
            duration: 0.4,
            ease: 'easeInOut',
        }, style: {
            bottom: controlbarHeight,
        }, children: _jsxs(motion.div, { className: "tribute-button-wrapper", layout: true, transition: {
                duration: 0.4,
                ease: 'easeInOut',
            }, children: [planAheadButton && (_jsx(ViewerPlanAheadButton, { ...rest, category: "PRE_PLANNING", onClick: handlePreNeedAction, status: userStatus, isPaused: isPaused })), filteredCommercialLinks && map(filteredCommercialLinks, (commercialLink, category) => (_createElement(ViewerImgButton, { ...rest, category: category, key: category, id: category, onClick: handleAnalyticClick, href: commercialLink, target: "_blank", status: userStatus, isPaused: isPaused })))] }, "tribute-button-wrapper") }, "tribute-button-block"), player);
};
// Function to map state to component props
const mapStateToProps = (state) => ({
    eventStatus: state.event.data.status,
});
export default connect(mapStateToProps)(ViewerCommercialLinks);
